import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { GlobalToastTypeNames, ToastTypes } from 'types';
import {
  useMatchFakturowniaInvoiceMutation,
  useSetFakturowniaInvoiceGroupMutation,
  useRerunFakturowniaMatcherMutation,
  useLinkFakturowniaInvoiceToPaymentMutation,
  useCreatePaymentForFakturowniaInvoiceMutation,
  useUnlinkFakturowniaInvoiceMutation,
  FakturowniaInvoicesDocument,
  FakturowniaUnmatchedCountDocument,
  FakturowniaInvoiceDocument,
} from 'core/graphql';

import { useToasts } from '../Toast/useToasts';

interface IInvoiceMatchingHook {
  /** @deprecated Phase 13 — legacy student-only match. Use `linkToPayment` or `createPayment` instead. */
  match: (
    invoiceId: string,
    studentId: string,
    groupId?: string,
  ) => Promise<boolean>;
  /** @deprecated Phase 13 — legacy group reassign. Use `linkToPayment` instead. */
  setGroup: (invoiceId: string, groupId: string | null) => Promise<boolean>;
  /** Re-runs the auto-matcher (now Payment-aware on the backend) for one invoice. */
  rerunMatcher: (invoiceId: string) => Promise<boolean>;
  /** Phase 13: pin invoice to an existing LMS Payment. Canonical linkage. */
  linkToPayment: (invoiceId: string, paymentId: string) => Promise<boolean>;
  /** Phase 13: create a Payment from invoice + agreement, then pin 1:1. */
  createPayment: (
    invoiceId: string,
    agreementId: string,
    paymentDate?: string,
  ) => Promise<boolean>;
  /** Phase 13: drop the Payment ↔ Invoice link (and legacy `linked_*`). */
  unlinkPayment: (invoiceId: string) => Promise<boolean>;
  loading: boolean;
}

/**
 * Bundles every linking mutation under one hook so pages don't need to wire
 * six separate hooks and copy-paste the toast plumbing. Each call refetches
 * the affected list/detail queries via Apollo's `refetchQueries` so the UI
 * stays in sync without manual cache patching.
 *
 * Phase 13: the three new Payment-aware mutations (`linkToPayment`,
 * `createPayment`, `unlinkPayment`) are the canonical path; the legacy
 * `match` / `setGroup` are kept here because the backend still serves them
 * as @deprecated stubs (they only write to `linked_*` legacy columns).
 */
export const useInvoiceMatching = (): IInvoiceMatchingHook => {
  const { t } = useTranslation();
  const { addToast } = useToasts();

  // Stable across renders so dependent useCallback hooks below stay stable too.
  const refetchQueries = useMemo(
    () => [
      { query: FakturowniaInvoicesDocument },
      { query: FakturowniaUnmatchedCountDocument },
    ],
    [],
  );

  const [matchMutation, matchState] = useMatchFakturowniaInvoiceMutation({
    refetchQueries,
    awaitRefetchQueries: true,
  });
  const [
    setGroupMutation,
    setGroupState,
  ] = useSetFakturowniaInvoiceGroupMutation({
    refetchQueries,
    awaitRefetchQueries: true,
  });
  const [rerunMutation, rerunState] = useRerunFakturowniaMatcherMutation({
    refetchQueries,
    awaitRefetchQueries: true,
  });
  const [
    linkPaymentMutation,
    linkPaymentState,
  ] = useLinkFakturowniaInvoiceToPaymentMutation({
    refetchQueries,
    awaitRefetchQueries: true,
  });
  const [
    createPaymentMutation,
    createPaymentState,
  ] = useCreatePaymentForFakturowniaInvoiceMutation({
    refetchQueries,
    awaitRefetchQueries: true,
  });
  const [
    unlinkPaymentMutation,
    unlinkPaymentState,
  ] = useUnlinkFakturowniaInvoiceMutation({
    refetchQueries,
    awaitRefetchQueries: true,
  });

  const notifySuccess = useCallback(
    (title: string) => {
      addToast({
        data: { type: ToastTypes.success, title },
        type: GlobalToastTypeNames.FakturowniaMatch,
      });
    },
    [addToast],
  );

  const notifyError = useCallback(
    (title: string, errors?: (string | null)[] | null) => {
      addToast({
        data: {
          type: ToastTypes.error,
          title,
          text: errors?.filter(Boolean).join('\n') || undefined,
        },
        type: GlobalToastTypeNames.FakturowniaMatch,
      });
    },
    [addToast],
  );

  const match = useCallback(
    async (invoiceId: string, studentId: string, groupId?: string) => {
      try {
        const result = await matchMutation({
          variables: { invoiceId, studentId, groupId },
          refetchQueries: [
            ...refetchQueries,
            { query: FakturowniaInvoiceDocument, variables: { id: invoiceId } },
          ],
        });
        const payload = result.data?.matchFakturowniaInvoice;
        if (payload?.success) {
          notifySuccess(t('pages.fakturownia.toasts.matched'));
          return true;
        }
        notifyError(t('pages.fakturownia.toasts.matchFailed'), payload?.errors);
        return false;
      } catch (error) {
        notifyError(t('pages.fakturownia.toasts.matchFailed'), [
          (error as Error).message,
        ]);
        return false;
      }
    },
    [matchMutation, refetchQueries, notifySuccess, notifyError, t],
  );

  const setGroup = useCallback(
    async (invoiceId: string, groupId: string | null) => {
      try {
        const result = await setGroupMutation({
          variables: { invoiceId, groupId: groupId || undefined },
          refetchQueries: [
            ...refetchQueries,
            { query: FakturowniaInvoiceDocument, variables: { id: invoiceId } },
          ],
        });
        const payload = result.data?.setFakturowniaInvoiceGroup;
        if (payload?.success) {
          notifySuccess(
            t(
              groupId
                ? 'pages.fakturownia.toasts.groupSet'
                : 'pages.fakturownia.toasts.groupCleared',
            ),
          );
          return true;
        }
        notifyError(t('pages.fakturownia.toasts.groupFailed'), payload?.errors);
        return false;
      } catch (error) {
        notifyError(t('pages.fakturownia.toasts.groupFailed'), [
          (error as Error).message,
        ]);
        return false;
      }
    },
    [setGroupMutation, refetchQueries, notifySuccess, notifyError, t],
  );

  const rerunMatcher = useCallback(
    async (invoiceId: string) => {
      try {
        const result = await rerunMutation({
          variables: { invoiceId },
          refetchQueries: [
            ...refetchQueries,
            { query: FakturowniaInvoiceDocument, variables: { id: invoiceId } },
          ],
        });
        const payload = result.data?.rerunFakturowniaMatcher;
        if (payload?.success) {
          notifySuccess(t('pages.fakturownia.toasts.rerunDone'));
          return true;
        }
        notifyError(t('pages.fakturownia.toasts.rerunFailed'), payload?.errors);
        return false;
      } catch (error) {
        notifyError(t('pages.fakturownia.toasts.rerunFailed'), [
          (error as Error).message,
        ]);
        return false;
      }
    },
    [rerunMutation, refetchQueries, notifySuccess, notifyError, t],
  );

  const linkToPayment = useCallback(
    async (invoiceId: string, paymentId: string) => {
      try {
        const result = await linkPaymentMutation({
          variables: { invoiceId, paymentId },
          refetchQueries: [
            ...refetchQueries,
            { query: FakturowniaInvoiceDocument, variables: { id: invoiceId } },
          ],
        });
        const payload = result.data?.linkFakturowniaInvoiceToPayment;
        if (payload?.success) {
          notifySuccess(t('pages.fakturownia.toasts.paymentLinked'));
          return true;
        }
        notifyError(
          t('pages.fakturownia.toasts.paymentLinkFailed'),
          payload?.errors,
        );
        return false;
      } catch (error) {
        notifyError(t('pages.fakturownia.toasts.paymentLinkFailed'), [
          (error as Error).message,
        ]);
        return false;
      }
    },
    [linkPaymentMutation, refetchQueries, notifySuccess, notifyError, t],
  );

  const createPayment = useCallback(
    async (invoiceId: string, agreementId: string, paymentDate?: string) => {
      try {
        const result = await createPaymentMutation({
          variables: {
            invoiceId,
            agreementId,
            paymentDate: paymentDate || undefined,
          },
          refetchQueries: [
            ...refetchQueries,
            { query: FakturowniaInvoiceDocument, variables: { id: invoiceId } },
          ],
        });
        const payload = result.data?.createPaymentForFakturowniaInvoice;
        if (payload?.success) {
          notifySuccess(t('pages.fakturownia.toasts.paymentCreated'));
          return true;
        }
        notifyError(
          t('pages.fakturownia.toasts.paymentCreateFailed'),
          payload?.errors,
        );
        return false;
      } catch (error) {
        notifyError(t('pages.fakturownia.toasts.paymentCreateFailed'), [
          (error as Error).message,
        ]);
        return false;
      }
    },
    [createPaymentMutation, refetchQueries, notifySuccess, notifyError, t],
  );

  const unlinkPayment = useCallback(
    async (invoiceId: string) => {
      try {
        const result = await unlinkPaymentMutation({
          variables: { invoiceId },
          refetchQueries: [
            ...refetchQueries,
            { query: FakturowniaInvoiceDocument, variables: { id: invoiceId } },
          ],
        });
        const payload = result.data?.unlinkFakturowniaInvoice;
        if (payload?.success) {
          notifySuccess(t('pages.fakturownia.toasts.paymentUnlinked'));
          return true;
        }
        notifyError(
          t('pages.fakturownia.toasts.paymentUnlinkFailed'),
          payload?.errors,
        );
        return false;
      } catch (error) {
        notifyError(t('pages.fakturownia.toasts.paymentUnlinkFailed'), [
          (error as Error).message,
        ]);
        return false;
      }
    },
    [unlinkPaymentMutation, refetchQueries, notifySuccess, notifyError, t],
  );

  return {
    match,
    setGroup,
    rerunMatcher,
    linkToPayment,
    createPayment,
    unlinkPayment,
    loading:
      matchState.loading ||
      setGroupState.loading ||
      rerunState.loading ||
      linkPaymentState.loading ||
      createPaymentState.loading ||
      unlinkPaymentState.loading,
  };
};
